import { predefinedTemplates } from 'constants/entities';
import { EntityStorages, MapEntity, MapEntityTypes } from 'types';
import { EntityWithRelations } from 'types/entities';

import { arrayUnion, createMapEntity, getEntityFullPath } from 'utils';

import { Entities, PredefinedTemplates, ProcessMapEntityFn } from '../types';

export const getMapEntityTemplateIds = (templates: PredefinedTemplates) => {
  const mapLayerTemplateId =
    templates[predefinedTemplates.MAP_LAYER]?.template?.id ?? 0;
  const mapObjectTemplateId =
    templates[predefinedTemplates.MAP_OBJECT]?.template?.id ?? 0;

  return { mapLayerTemplateId, mapObjectTemplateId };
};

export const getMapEntities = (
  layerTemplateId: number,
  objectTemplateId: number,
  entities: EntityWithRelations[],
  state: MapEntity['state'],
  processEntityFn?: ProcessMapEntityFn
): Entities =>
  entities.reduce((acc, curr) => {
    const entityId = curr.entity.id;
    const mapEntity = createMapEntity(
      layerTemplateId,
      objectTemplateId,
      curr,
      state
    );

    const processedMapEntity = processEntityFn?.(mapEntity);

    return {
      ...acc,
      [entityId]: processedMapEntity ?? mapEntity,
    };
  }, {});

export const processExistingMapEntity = (
  entities: Entities,
  mapEntity: MapEntity,
  keepPrev?: { storage?: boolean; active?: boolean }
) => {
  const prevEntity = entities[mapEntity.entity.id];

  return {
    ...mapEntity,
    state: {
      ...mapEntity.state,
      storage: keepPrev?.storage
        ? prevEntity?.state?.storage ?? mapEntity.state.storage
        : mapEntity.state.storage,
      active: keepPrev?.active
        ? prevEntity?.state?.active ?? mapEntity.state.active
        : mapEntity.state.active,
    },
  };
};

export const processSharedMapEntity = (
  mapEntity: MapEntity,
  sharedEntities: EntityWithRelations[],
  sharedEntityId: number
) => {
  const entityFullPath = getEntityFullPath(
    sharedEntities,
    mapEntity.parentIDs[0]
  );

  return {
    ...mapEntity,
    state: {
      ...mapEntity.state,
      active:
        mapEntity.entity.id === sharedEntityId ||
        entityFullPath.includes(sharedEntityId),
    },
  };
};

export const processInscribedMapEntity = (
  mapEntity: MapEntity,
  entities: Entities
) => {
  const prevMapEntity = entities[mapEntity.entity.id];

  if (prevMapEntity) {
    const entity = { ...prevMapEntity };

    entity.info.type === MapEntityTypes.LAYER &&
      (entity.childIDs = mapEntity.childIDs);
    entity.info.type === MapEntityTypes.OBJECT && (entity.state.active = true);

    return entity;
  } else {
    const entity = {
      ...mapEntity,
    };

    entity.info.type === MapEntityTypes.OBJECT && (entity.state.active = true);

    return entity;
  }
};

export const processOpenedMapFolderEntityChild = (
  entities: Entities,
  mapEntity: MapEntity,
  targetId: number
) => {
  const prevEntity = entities[mapEntity.entity.id];
  const upmostParentEntity = entities[targetId];

  return {
    ...mapEntity,
    childIDs: arrayUnion(prevEntity?.childIDs ?? [], mapEntity.childIDs),
    state: {
      ...mapEntity.state,
      storage: upmostParentEntity?.queryFilterMatched
        ? EntityStorages.FULL
        : mapEntity.entity.id === targetId
        ? EntityStorages.PARTIAL
        : EntityStorages.NONE,
      active: !!prevEntity?.state?.active,
    },
  };
};

export const processCreatedMapEntity = (
  entities: Entities,
  mapEntity: MapEntity,
  targetId: number
) => {
  const prevEntity = entities[mapEntity.entity.id];

  return {
    ...mapEntity,
    state: {
      ...mapEntity.state,
      active: mapEntity.entity.id === targetId || !!prevEntity?.state?.active,
    },
  };
};
